import { RouterProvider } from "@tanstack/react-router";

import { ElectronBrowserHost } from "./browser/ElectronBrowserHost";
import { CloudBrowserKeepAwake } from "./components/preview/CloudBrowserKeepAwake";
import { PreviewAutomationHosts } from "./components/preview/PreviewAutomationHosts";
import { AnalyticsBridge } from "./analytics/AnalyticsBridge";
import { AnalyticsConsent } from "./analytics/AnalyticsConsent";
import { RenderErrorBoundary } from "./components/RenderErrorBoundary";
import { ReportedErrorScreen } from "./components/ReportedErrorScreen";
import { AppAtomRegistryProvider } from "./rpc/atomRegistry";
import type { AppRouter } from "./router";

/**
 * Owns renderer-wide providers. The Electron browser host intentionally sits
 * outside the router so its webviews survive route transitions, but it must
 * share the same atom registry as routed UI.
 *
 * The boundary is the last line: a crash outside the route tree (a bridge
 * hook, a host) would otherwise leave a blank page. Nothing below it can be
 * retried in place, so its way back is a reload.
 */
export function AppRoot({ router }: { readonly router: AppRouter }) {
  return (
    <RenderErrorBoundary
      fallback={<ReportedErrorScreen onRetry={() => window.location.reload()} />}
    >
      <AppAtomRegistryProvider>
        <RouterProvider router={router} />
        <AnalyticsBridge router={router} />
        <AnalyticsConsent />
        <PreviewAutomationHosts />
        <CloudBrowserKeepAwake />
        <ElectronBrowserHost />
      </AppAtomRegistryProvider>
    </RenderErrorBoundary>
  );
}
